import { z } from "zod/v4";

/**
 * Extracts parameter names from a template string at the type level.
 * @example ExtractParams<"{id}.example.com"> // "id"
 * @example ExtractParams<"{account}.{region}.example.com"> // "account" | "region"
 * @example ExtractParams<"example.com"> // never
 */
type ExtractParams<T extends string> =
  T extends `${string}{${infer Param}}${infer Rest}` ? Param | ExtractParams<Rest> : never;

/**
 * Placeholder substituted by `RealmId.toFqdn` with the realm subdomain.
 * Preserved through `resolveEnvironment` so realm-specific templates can be filled per-call.
 */
export const REALM_FQDN_PLACEHOLDER = "{realm}";
const REALM_FQDN_PARAM = "realm" as const;

type EnvironmentDefinition = {
  /** FQDN template for the environment root (directory) with optional {param} placeholders. */
  readonly fqdn: string;
  /**
   * FQDN template for realm zones. Always contains `{realm}`, which `RealmId.toFqdn` fills in;
   * any other `{param}` placeholders are resolved by `resolveEnvironment` alongside `fqdn`.
   *
   * MR and Prd/Int use different shapes: in MRs the realm is a sibling of the root zone
   * (`{mergeRequestId}-{realm}.a3s-int.visioni.network`) rather than a child of it, so
   * `fqdn` alone is not enough to derive the realm FQDN — hence a separate template.
   */
  readonly realmFqdn: string;
  /** Vision Auth client ID */
  readonly authClientId: string;
  /** Vision Auth audience */
  readonly authAudience: string;
  /** Vision Auth domain */
  readonly authDomain: string;
};

/**
 * Exhaustive environment definitions.
 * Static environments have fixed FQDNs, dynamic ones use `{param}` placeholders.
 */
export const ENVIRONMENT_DEFINITIONS = {
  prd: {
    fqdn: "a3s.visioni.network",
    realmFqdn: "{realm}.a3s.visioni.network",
    authClientId: "YQfWpFmUedlCFmz4Cylw8fdBHZo1GU8o",
    authAudience: "a3s-prd-api",
    authDomain: "auth.visioni.network",
  },
  int: {
    fqdn: "a3s-int.visioni.network",
    realmFqdn: "{realm}.a3s-int.visioni.network",
    authClientId: "PArgOiKxKLY09LmcxnPPMHb4Wx16CVGM",
    authAudience: "a3s-int-api",
    authDomain: "auth.visioni.network",
  },
  mr: {
    fqdn: "{mergeRequestId}.a3s-int.visioni.network",
    // Realm is a sibling of the root zone, not a child — needed so an Auth0 wildcard
    // at `*.a3s-int.visioni.network` can cover every MR's realm origins.
    realmFqdn: "{mergeRequestId}-{realm}.a3s-int.visioni.network",
    // MR environments share int's auth configuration
    authClientId: "PArgOiKxKLY09LmcxnPPMHb4Wx16CVGM",
    authAudience: "a3s-int-api",
    authDomain: "auth.visioni.network",
  },
  dev: {
    fqdn: "{account}.a3s-dev.visioni.network",
    realmFqdn: "{realm}.{account}.a3s-dev.visioni.network",
    authClientId: "DyP7xofTYokB67a1oBx8BbEM3Vg7bUiR",
    authAudience: "a3s-dev-api",
    authDomain: "auth.visioni.network",
  },
} as const satisfies Record<string, EnvironmentDefinition>;

/** All known environment keys. */
export type EnvironmentKey = keyof typeof ENVIRONMENT_DEFINITIONS;

export const isEnvironmentKey = (value: unknown): value is EnvironmentKey =>
  typeof value === "string" && value in ENVIRONMENT_DEFINITIONS;

/** Case-insensitive Zod codec that normalises to a canonical lowercase EnvironmentKey on decode. */
export const EnvironmentKey = z.codec(
  z.string(),
  z.custom<EnvironmentKey>(() => true),
  {
    decode: (encoded, ctx) => {
      const lower = encoded.toLowerCase();
      if (!isEnvironmentKey(lower)) {
        ctx.issues.push({
          code: "custom",
          input: encoded,
          message: `Invalid environment key: "${encoded}". Expected one of: ${Object.keys(ENVIRONMENT_DEFINITIONS).join(", ")}`,
        });
        return z.NEVER;
      }
      return lower;
    },
    encode: (decoded) => decoded,
  },
);

/**
 * Extract required parameters for a given environment key.
 *
 * Covers both `fqdn` and `realmFqdn` templates so that placeholders appearing only in
 * `realmFqdn` (apart from `{realm}`, which is filled in later by `RealmId.toFqdn`) are
 * still required at the type level.
 */
export type ParamsFor<K extends EnvironmentKey> = Exclude<
  ExtractParams<(typeof ENVIRONMENT_DEFINITIONS)[K]["fqdn"] | (typeof ENVIRONMENT_DEFINITIONS)[K]["realmFqdn"]>,
  typeof REALM_FQDN_PARAM
>;

/** A resolved environment entry. All per-environment params are substituted; `{realm}` remains in `realmFqdn`. */
export type EnvironmentEntry = {
  /** The environment key (e.g., "prd", "int", "mr", "dev") */
  readonly key: EnvironmentKey;
  /** Root FQDN for A3S environment (for example, "a3s-int.visioni.network") */
  readonly fqdn: string;
  /**
   * Realm FQDN template with `{realm}` still to be filled by `RealmId.toFqdn`.
   * All other placeholders (e.g. `{mergeRequestId}`, `{account}`) are already resolved.
   * @example "{realm}.a3s-int.visioni.network", "628-{realm}.a3s-int.visioni.network"
   */
  readonly realmFqdn: string;
  /** Vision Auth client ID */
  readonly authClientId: string;
  /** Vision Auth audience */
  readonly authAudience: string;
  /** Vision Auth domain */
  readonly authDomain: string;
};

type EnvironmentSet<K extends string = string> = Readonly<Record<K, EnvironmentEntry>>;

/**
 * Configuration schema for specifying which environment to use.
 * Static environments need only the key; dynamic ones require params.
 */
export const EnvironmentConfig = z.discriminatedUnion("default", [
  z.object({ default: z.literal("prd") }),
  z.object({ default: z.literal("int") }),
  z.object({ default: z.literal("mr"), params: z.object({ mergeRequestId: z.string().min(1) }) }),
  z.object({ default: z.literal("dev"), params: z.object({ account: z.string().min(1) }) }),
]);

export type EnvironmentConfig = z.infer<typeof EnvironmentConfig>;

/**
 * Single source of truth for EnvironmentConfig → bus-name-safe prefix.
 * Used in both CDK (synth time) and Lambda (runtime) to ensure EventBridge
 * bus names match between publisher and subscriber.
 */
export function environmentPrefix(config: EnvironmentConfig): string {
  switch (config.default) {
    case "prd":
      return "prd";
    case "int":
      return "int";
    case "mr":
      return `mr${config.params.mergeRequestId}`;
    case "dev":
      return `dev${config.params.account}`;
    default:
      return config satisfies never;
  }
}

// Compile-time assertion: schema keys must match definition keys exactly
type _Equals<A, B> =
  [A] extends [B] ?
    [B] extends [A] ?
      true
    : never
  : never;
true satisfies _Equals<EnvironmentConfig["default"], EnvironmentKey>;

/**
 * Resolves an environment definition to a concrete entry by substituting template params.
 */
export function resolveEnvironment<K extends EnvironmentKey>(
  key: K,
  ...args: [ParamsFor<K>] extends [never] ? [] : [params: Record<ParamsFor<K>, string>]
): EnvironmentEntry {
  const def = ENVIRONMENT_DEFINITIONS[key];
  const params = args[0] as Record<string, string> | undefined;

  // `{realm}` is intentionally preserved; it is filled in later by `RealmId.toFqdn`.
  const substitute = (template: string): string =>
    template.replace(/{(\w+)}/g, (match, paramName: string) => {
      if (paramName === REALM_FQDN_PARAM) return match;
      const value = params?.[paramName];
      if (value === undefined) {
        throw new Error(`Missing required parameter '${paramName}' for environment '${key}'`);
      }
      return value;
    });

  return {
    key,
    fqdn: substitute(def.fqdn),
    realmFqdn: substitute(def.realmFqdn),
    authClientId: def.authClientId,
    authAudience: def.authAudience,
    authDomain: def.authDomain,
  };
}

/** Available environments and the default environment to use. */
export type Environments<K extends string = string> = {
  readonly available: EnvironmentSet<K>;
  readonly default: K;
};

/**
 * Resolves an EnvironmentConfig to an Environments object with the resolved entry.
 */
export function resolveConfig(config: EnvironmentConfig): Environments {
  const prd = resolveEnvironment("prd");
  const int = resolveEnvironment("int");

  const entry = (() => {
    switch (config.default) {
      case "prd":
        return prd;
      case "int":
        return int;
      case "mr":
        return resolveEnvironment("mr", config.params);
      case "dev":
        return resolveEnvironment("dev", config.params);
      default:
        return config satisfies never;
    }
  })();

  return {
    available: { prd, int, [config.default]: entry },
    default: config.default,
  };
}
